import { useEffect, useState } from "react";
import { ImageOff } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export interface ThumbProps {
  url: string | undefined;
  /** Cómo se llama lo que se está enseñando: es lo que se lee si falla. */
  name: string;
  size?: "sm" | "lg";
  className?: string;
}

/**
 * La miniatura de una fila o de una vista previa.
 *
 * Las imágenes son URLs escritas a mano —todavía no hay subida de
 * archivos— así que una URL rota no es un caso raro: es el caso normal
 * cuando alguien pega mal el enlace. Si la imagen falla, la fila NO se
 * queda con el icono roto del navegador: se cae al hueco con icono, que
 * además le dice a quien administra que ahí hay algo que arreglar.
 *
 * El `alt` es vacío porque el nombre ya está en la celda de al lado:
 * repetirlo hace que un lector de pantalla lo diga dos veces.
 *
 * Es la versión genérica de `products/components/ProductThumb.tsx` —
 * misma lógica, sin saber qué se está enseñando. Aquella sigue en pie
 * porque su carpeta es de otro agente; es redundante a partir de ahora.
 */
const sizeClasses = {
  sm: "h-11 w-11 rounded-xl",
  lg: "h-28 w-28 rounded-2xl",
};

export function Thumb({ url, name, size = "sm", className }: ThumbProps) {
  const [failed, setFailed] = useState(false);

  // Si cambia la URL, la anterior ya no manda: hay que volver a intentar.
  useEffect(() => setFailed(false), [url]);

  const shell = cn(
    "shrink-0 overflow-hidden border border-ds-border bg-ds-cream-2",
    sizeClasses[size],
    className,
  );

  if (!url || failed) {
    return (
      <div
        className={cn(shell, "flex items-center justify-center")}
        title={name}
        aria-hidden="true"
      >
        <ImageOff className="h-4 w-4 text-ds-ink-3" />
      </div>
    );
  }

  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className={cn(shell, "object-cover")}
    />
  );
}
